/**
 * `ServerGameClient` — la simulation **autoritative**, parlée en HTTP.
 *
 * Même contrat que le bac à sable (`GameClient`, `{getState, dispatch}`), même signatures
 * asynchrones : aucun composant ne sait lequel des deux il utilise. C'est ce que promettait
 * l'en-tête de `gameClient.ts` depuis le premier jour, et c'est ce fichier qui l'honore.
 *
 * ## Ce qui change vraiment par rapport au bac à sable
 *
 * | | Bac à sable | Compte |
 * |---|---|---|
 * | Qui calcule | le moteur, dans l'onglet | le moteur, **sur le serveur** |
 * | Où vit l'état | `localStorage`, réinscriptible | SQLite, en transaction (§7.1) |
 * | Chute en mode normal | termine la run (écart connu) | **retour au jalon** (§7.3) |
 * | Victoire | jamais gravée, score `null` | gravée, score /1000, classement (§7.4) |
 * | Hardcore | trichable en 30 s | verrouillé dans la transaction |
 *
 * ## Les trois règles de sûreté réseau, et pourquoi elles suffisent
 *
 * 1. **L'`action_id` est tiré AVANT l'envoi et gardé pendant les reprises** (§7.2). Une réponse
 *    perdue n'est pas une action perdue : réémettre le même `action_id` fait renvoyer au serveur
 *    le `result` qu'il a stocké, **bit-à-bit identique**, sans re-résoudre. C'est la seule chose
 *    qui rend un retry sûr dans un jeu où chaque action consomme des ressources.
 * 2. **On ne rejoue QUE sur `ErreurReseau`.** Une erreur d'API est une décision du serveur :
 *    la répéter ne changerait rien et pourrait, elle, faire du dégât.
 * 3. **Un `409 expected_turn_mismatch` n'est jamais résolu tout seul.** `details.current_turn`
 *    dit que quelqu'un d'autre (un second onglet) a joué : on refait `GET /api/runs/active`, on
 *    remplace l'état en bloc, et on **rend la main au joueur**. Rejouer son action au nouveau
 *    tour serait décider à sa place — l'état sur lequel il avait cliqué n'existe plus.
 *
 * ## Les manques signalés hier, comblés aujourd'hui
 *
 * Ils ont tous été réparés **côté serveur** — c'est-à-dire au bon endroit. Ce qui reste ici,
 * c'est le branchement :
 *
 * - **`log`** et **`recap`** ⟦A-16⟧ arrivent maintenant dans le corps de `POST /actions`. Le
 *   journal reprend donc la trame que le moteur a écrite (« Nuit 3 — énergie remise à 100 »)
 *   au lieu de la reconstituer depuis les `events`, et le panneau des jours écoulés s'ouvre en
 *   mode compte comme en bac à sable.
 * - **`production_value`** ⟦A-11 §3⟧, **`acheter_objet`**, **`retour_parents`** et
 *   **`reprendre_studio`** existent dans l'union Zod du serveur (19 types). Les quatre refus
 *   « le transport ne sait pas porter ça » ont disparu avec eux.
 * - **`POST /api/runs/:id/abandon`** existe : abandonner ferme vraiment la run (`ended`,
 *   `abandoned`) au lieu de l'oublier localement — sinon `/runs/active` la remettait dans les
 *   mains du joueur au rechargement suivant, et `POST /runs` le refusait en `409`.
 */

import {
  InvalidAction,
  type Action,
  type ActionType,
  type GameState,
  type RunMode,
} from "@maxime/shared";

import type { SoumissionAtelier } from "../api/atelier";
import { ErreurApi, ErreurReseau, attendre, uuid } from "../api/http";
import {
  abandonnerRun,
  creerRun as apiCreerRun,
  posterAction,
  runsActives,
  type EnveloppeAction,
  type ReponseAction,
  type RunActive,
  type RunApi,
} from "../api/jeu";
import { nouveauxEtages, etage, type EtageId } from "./escalier";
import type {
  ContexteAction,
  CreationRun,
  DispatchResult,
  GameClient,
  GenreAvatar,
  JournalEntry,
  Progression,
  RunSnapshot,
} from "./gameClient";
import { entreeInterface, entreesDeResolution, ligneDeverrouillage, limiterJournal } from "./journal";

// ─────────────────────────────────────────────────────────────────────────────
// Ce que le client garde en local — et **rien qui décide du jeu**
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Le journal affiché et les étages déjà annoncés sont de la **mémoire d'interface** : le
 * serveur n'a pas de colonne pour eux, et il n'en a pas besoin (il porte, lui, `events_log`,
 * qui est la vérité). Les perdre ne change rien à la partie ; les garder évite de re-annoncer
 * les six étages de l'escalier à chaque F5. On les range donc à côté, par run.
 *
 * Rien d'autre ne transite par ce cache. En particulier **aucun morceau de `GameState`** : le
 * miroir vient toujours du serveur, à chaque chargement.
 */
const CLE_META = "maxime.compte.meta.v1";

interface MetaRun {
  journal: JournalEntry[];
  unlocksSeen: EtageId[];
}

interface MetaLocale {
  /** La dernière run jouée — quand le compte en a deux (une par mode), on reprend celle-là. */
  dernier: string | null;
  runs: Record<string, MetaRun>;
}

function lireMeta(): MetaLocale {
  try {
    const brut = localStorage.getItem(CLE_META);
    if (!brut) return { dernier: null, runs: {} };
    const meta = JSON.parse(brut) as Partial<MetaLocale>;
    return { dernier: meta.dernier ?? null, runs: meta.runs ?? {} };
  } catch {
    return { dernier: null, runs: {} };
  }
}

function ecrireMeta(meta: MetaLocale): void {
  try {
    localStorage.setItem(CLE_META, JSON.stringify(meta));
  } catch {
    // Quota plein ou navigation privée : on perd le journal affiché, jamais la partie.
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦A19⟧ Le vocabulaire — moteur → API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Une action que **ce transport** ne sait pas porter. Ce n'est ni un refus du moteur (qui, lui,
 * arrive en `422 engine_rejected`) ni un bug : c'est un trou connu du contrat, nommé.
 *
 * `messageErreur` rend le `message` d'une `Error` quelconque : le joueur lit donc une phrase
 * française, pas un code HTTP.
 */
export class ActionIndisponible extends Error {
  constructor(
    readonly action: ActionType,
    message: string,
  ) {
    super(message);
    this.name = "ActionIndisponible";
  }
}

/**
 * Les timelines d'une production, ou un refus **avant** l'aller-retour réseau.
 *
 * Sans elles, le serveur répondrait `422 validation_error` sur le champ `atelier` manquant — un
 * message technique, après que le joueur a joué cinq mini-jeux. Le trou est ici, il se dit ici.
 */
function exigerTimelines(type: ActionType, contexte?: ContexteAction): SoumissionAtelier {
  if (!contexte?.atelier) {
    throw new ActionIndisponible(
      type,
      "Cette production n'a pas de gestes à soumettre : le serveur note l'Atelier lui-même, " +
        "il ne peut rien faire d'un score. Rejoue les étapes de cette vidéo.",
    );
  }
  return contexte.atelier;
}

/** L'identité d'une vidéo côté base : `"{run_id}:{id moteur}"` (décision de `routes/actions.ts`). */
function idVideoApi(runId: string, idMoteur: number): string {
  return `${runId}:${idMoteur}`;
}

/** L'inverse — tolérant, comme côté serveur : un id d'une autre run rend `null`. */
export function idMoteurDepuisApi(runId: string, idApi: string): number | null {
  const prefixe = `${runId}:`;
  if (!idApi.startsWith(prefixe)) return null;
  const n = Number(idApi.slice(prefixe.length));
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Traduit une action du **moteur** vers le vocabulaire de l'**API** — l'exact miroir de
 * `versActionMoteur` côté serveur (⟦A19⟧ : « les deux specs ont été écrites dans deux langues,
 * aucune ne bouge, la couture est ici »).
 *
 * Sept renommages, un aiguillage de format (`longue` → `long`), et **une seule vraie
 * traduction** : une production échange ses `scores` (que le serveur ne lirait pas) contre ses
 * `atelier` (les gestes, qu'il rescore lui-même).
 *
 * ⟦A-11⟧ `acheter_objet`, `retour_parents` et `reprendre_studio` tombent dans le `default:` :
 * les noms sont identiques des deux côtés depuis que l'union de l'API est passée à 19 types.
 */
export function versActionApi(
  action: Action,
  runId: string,
  contexte?: ContexteAction,
): Record<string, unknown> {
  switch (action.type) {
    /**
     * ⚠️ **Les `scores` de l'action moteur ne partent PAS.** C'est le cœur du modèle : l'union
     * d'action du serveur n'a aucun champ pour les porter, elle attend `atelier` — les gestes.
     * Il régénère le pattern du tour et recalcule les cinq notes (§6.4). Ce que le joueur a vu à
     * l'écran est une **prévision** locale, jamais un verdict, et c'est ce qui rend le classement
     * et la victoire gravée dignes de foi.
     */
    case "produce_video": {
      const atelier = exigerTimelines(action.type, contexte);
      // ⟦A-11 §3⟧ `0` ⇒ champ **absent**, jamais `0` explicite : c'est l'état neutre, celui où
      // la table des coûts et le plafond sont ceux d'avant l'avenant (⟦A-10⟧).
      const pv =
        action.production_value !== undefined && action.production_value > 0
          ? { production_value: action.production_value }
          : {};
      return action.format === "short"
        ? {
            type: "produce_video",
            kind: "short",
            subject_id: action.subject_id,
            evergreen_attempt: false,
            extra_card_subjects: [],
            ...pv,
            atelier,
          }
        : {
            type: "produce_video",
            kind: "long",
            subject_id: action.subject_id,
            evergreen_attempt: action.evergreen_intent,
            extra_card_subjects: action.extra_card_subjects,
            ...pv,
            atelier,
          };
    }
    case "video_jalon":
      return {
        type: "milestone_video",
        milestone_id: action.jalon_id,
        atelier: exigerTimelines(action.type, contexte),
      };
    case "menage":
      return { type: "library_cleanup", video_id: idVideoApi(runId, action.video_id) };
    case "remaster":
      return { type: "library_remaster", video_id: idVideoApi(runId, action.video_id) };
    case "ranger_serie":
      return {
        type: "library_series",
        video_ids: action.video_ids.map((id) => idVideoApi(runId, id)),
        // §5.5 dit `series_id`, SPEC-MOTEUR §2.2 dit `series_name` : la même chaîne, deux noms.
        series_id: action.series_name,
      };
    case "acheter_skill":
      return { type: "buy_skill", skill_id: action.skill_id };
    case "choix_evenement":
      return { type: "event_choice", event_id: action.event_id, choice_id: action.choice_id };
    case "lire_commentaires":
      // 🔴 ⟦SPEC-FIL §9.6 · M3.23⟧ **La charge utile s'arrête ICI en mode compte, et c'est le
      //    dernier maillon manquant du Fil.** Le moteur traite `traitements`, l'écran les
      //    compose, le bac à sable les joue — mais la route `POST /actions` du serveur les
      //    ignore : `server/src/routes/actions.ts` valide `read_comments` en `.strict()` sans
      //    ce champ (l. 135) et le reconstruit sans lui (l. 399).
      //
      //    Les deux lignes appartiennent à une AUTRE session (périmètre `server/src/routes/`),
      //    et c'est la raison pour laquelle `content/meta.json → fil_actif` reste à `false` :
      //    allumer maintenant donnerait un écran dont les cinq boutons ne feraient rien sur un
      //    compte, en silence. Un écran qui ment est pire que l'absence d'écran — c'est le
      //    défaut que ce projet a déjà payé neuf fois.
      //
      //    Le champ n'est envoyé que s'il est NON VIDE : tant que le Fil est éteint il l'est
      //    toujours, donc cette ligne ne change strictement rien aujourd'hui ⟦A-10⟧. Le jour où
      //    le serveur l'accepte, le mode compte fonctionne sans retoucher le client.
      return action.traitements !== undefined && action.traitements.length > 0
        ? { type: "read_comments", traitements: action.traitements }
        : { type: "read_comments" };
    case "pause":
      return { type: "take_break" };
    case "therapie":
      return { type: "therapy" };
    case "clore_run":
      return { type: "close_victory" };

    // Vocabulaire déjà commun : `sleep`, `veille`, `respec`, `annoncer_pause`,
    // `continuer_apres_rue`, et depuis ⟦A-11⟧ `retour_parents` et `reprendre_studio`.
    case "acheter_objet":
      return { type: "acheter_objet", objet_id: action.objet_id };
    // ⟦SPEC-FIL §9.5 · A-82⟧ La charge utile du community manager. Sans ce cas, le `default`
    // aurait envoyé `{ type: "regler_cm" }` sans `actif` — et le schéma l'aurait refusé.
    case "regler_cm":
      return { type: "regler_cm", actif: action.actif };
    default:
      return { type: action.type };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Le client
// ─────────────────────────────────────────────────────────────────────────────

/** Nombre total de tentatives d'un même `action_id` avant d'abandonner (§7.2). */
const TENTATIVES = 3;

/**
 * Une désynchronisation avérée : un autre onglet a joué, l'état a été rechargé, et l'action du
 * joueur n'a **pas** été appliquée. Volontairement une `Error` simple — son message est déjà
 * la phrase qu'on veut montrer.
 */
export class Desynchronise extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Desynchronise";
  }
}

export class ServerGameClient implements GameClient {
  readonly kind = "serveur" as const;

  /**
   * `production: true` depuis que l'Atelier consomme `GET /atelier-pattern` et soumet ses
   * timelines. Le serveur n'a jamais été la limite — son scoring était livré et testé ; c'était
   * la **couture** qui manquait.
   */
  readonly capacites = { production: true } as const;

  #run: RunSnapshot | null = null;
  /**
   * Le tour **selon le serveur**. On ne lit pas `state.turn` : c'est le compteur de `run_state`
   * qui pilote `expected_turn` (§7.2), et c'est la réponse qui en fait foi. Les deux coïncident
   * aujourd'hui ; s'appuyer sur celui du moteur reviendrait à parier là-dessus pour toujours.
   */
  #turn = 0;

  getState(): RunSnapshot | null {
    return this.#run;
  }

  /**
   * §5.3 — restitution au chargement. Jamais de `404` : pas de run ⇒ `null`.
   *
   * ⚠️ **Un trou d'écran, pas de transport** : `/runs/active` ne rend que les runs *jouables*.
   * Une run terminée (hardcore, ou victoire close) en sort — donc après un F5, le joueur ne
   * revoit pas son écran de fin, il retombe sur la création. Le mémorial existe pourtant
   * entièrement côté serveur (`GET /api/runs/:id`, épitaphe comprise) et `GET /api/me` en donne
   * l'index : il manque **l'écran**, pas la donnée. À prendre avec `/memorial` de SPEC-UI §3.7.
   */
  async charger(): Promise<RunSnapshot | null> {
    const { runs } = await runsActives();
    if (runs.length === 0) {
      this.#run = null;
      return null;
    }
    const meta = lireMeta();
    // Un compte peut avoir deux runs jouables (une par mode, §5.2). On reprend celle qu'on
    // jouait ; à défaut, la plus ancienne — l'ordre du serveur, pas un tri inventé.
    const choisie = runs.find((r) => r.run.id === meta.dernier) ?? runs[0]!;
    return this.#adopter(choisie);
  }

  /** §5.2 — créer une run. La graine est tirée **par le serveur** et ne revient jamais. */
  async creerRun(input: CreationRun): Promise<RunSnapshot> {
    try {
      const { run, turn, state } = await apiCreerRun({
        mode: input.mode,
        channel_name: input.channelName.trim(),
        genre: input.genre ?? undefined,
        assist_tolerance: input.toleranceAssistee ?? false,
      });
      const snapshot = this.#composer(run, state, {
        journal: [
          entreeInterface(state, 1, {
            icone: "🎬",
            ton: "info",
            texte: `« ${run.channel_name} » est née. Zéro abonné, une caméra, et une rue à décrocher.`,
          }),
        ],
        unlocksSeen: [],
      });
      this.#poser(snapshot, turn);
      return snapshot;
    } catch (e) {
      // §5.2 — `409 active_run_exists` : la run existe déjà pour ce mode. La bonne réponse n'est
      // pas une erreur, c'est de la **reprendre** : côté compte, une run ne se jette pas, et
      // l'utilisateur vient précisément de dire qu'il voulait jouer.
      if (e instanceof ErreurApi && e.code === "active_run_exists") {
        const reprise = await this.charger();
        if (reprise) return reprise;
      }
      throw e;
    }
  }

  /** §5.5 — LE point de passage de toute action de jeu. */
  async dispatch(action: Action, contexte?: ContexteAction): Promise<DispatchResult> {
    const run = this.#run;
    if (!run) throw new Error("Aucune run en cours.");

    const enveloppe: EnveloppeAction = {
      expected_turn: this.#turn,
      // ⚠️ Tiré **maintenant**, avant le premier envoi, et réutilisé tel quel par chaque
      // reprise : c'est toute l'idempotence de §7.2 qui tient à cette ligne.
      action_id: uuid(),
      action: versActionApi(action, run.runId, contexte),
    };

    const reponse = await this.#envoyer(run.runId, enveloppe);
    return this.#appliquer(run, reponse, action.type);
  }

  /**
   * Quitter la run — **et le dire au serveur**.
   *
   * Il n'existe toujours aucune route de suppression, et c'est délibéré : une run terminée est
   * le mémorial (PLAN §4.11), elle n'est jamais effacée. `POST /runs/:id/abandon` la ferme
   * (`ended` / `abandoned`) : elle sort de `/runs/active` et libère la place que §5.2 réserve à
   * une seule run jouable par mode. Sans cet appel, l'oubli n'était que local — au rechargement
   * suivant la run revenait, et créer la suivante rendait `409 active_run_exists`.
   *
   * ⚠️ **Un échec de l'appel interrompt tout, et c'est voulu** — ce commentaire disait
   * exactement le contraire jusqu'⟦A-97⟧ (« on oublie quand même la run localement »), alors
   * que le code relance depuis toujours. Seul le `409 run_not_active` est ravalé : la run était
   * déjà terminée, donc l'abandon a bien eu lieu et il n'y a rien à réessayer. Tout le reste
   * (réseau coupé, `403`, `404`) remonte à l'appelant **avant** le nettoyage local, si bien que
   * la run reste en place des deux côtés. C'est la seule version honnête : oublier la run
   * localement alors que le serveur la garde `active` afficherait « c'est fait » à un joueur
   * dont la chaîne est toujours vivante — et `POST /runs` lui rendrait `409 active_run_exists`
   * à la partie suivante. L'écran qui appelle doit donc attraper (`Reglages.tsx` ⟦A-97 §1.2⟧).
   */
  async abandonner(): Promise<void> {
    const courante = this.#run;
    if (courante) {
      try {
        await abandonnerRun(courante.runId);
      } catch (e) {
        // `409 run_not_active` : elle était déjà terminée — l'abandon a donc bien eu lieu.
        if (!(e instanceof ErreurApi && e.code === "run_not_active")) throw e;
      }
    }
    const meta = lireMeta();
    if (courante) delete meta.runs[courante.runId];
    meta.dernier = null;
    ecrireMeta(meta);
    this.#run = null;
    this.#turn = 0;
  }

  // ── Interne ──────────────────────────────────────────────────────────────

  /**
   * Envoie l'enveloppe, en rejouant **le même `action_id`** tant que c'est le réseau qui tombe.
   * Traduit les refus du serveur en erreurs que l'interface sait déjà montrer.
   */
  async #envoyer(runId: string, enveloppe: EnveloppeAction): Promise<ReponseAction> {
    for (let tentative = 1; ; tentative++) {
      try {
        return await posterAction(runId, enveloppe);
      } catch (e) {
        if (e instanceof ErreurReseau && tentative < TENTATIVES) {
          // 300 ms, puis 600 : assez pour traverser une micro-coupure, assez court pour que le
          // joueur ne croie pas l'interface figée (le bouton est déjà désactivé par `pending`).
          await attendre(300 * tentative);
          continue;
        }
        throw await this.#traduireRefus(e);
      }
    }
  }

  /**
   * Les quatre familles de refus de §5.5, chacune rendue au bon type.
   *
   * ⚠️ **On teste `details.code`, jamais le message** : le serveur envoie une phrase générique
   * (« Cette action n'est pas jouable en l'état ») et le vrai code du moteur dans `details`,
   * parce que `InvalidAction` fait `super(code)` — son message *est* un identifiant. En le
   * reconstruisant en `InvalidAction`, tout le client existant (`game/erreurs.ts`, exhaustif par
   * `Record<InvalidActionCode, …>`) continue de parler français sans une ligne de plus.
   */
  async #traduireRefus(e: unknown): Promise<unknown> {
    if (!(e instanceof ErreurApi)) return e;

    if (e.code === "validation_error" && e.raison === "engine_rejected") {
      const code = e.details.code;
      if (typeof code === "string") {
        const { code: _ignore, reason: _aussi, ...details } = e.details;
        return new InvalidAction(
          code as InvalidAction["code"],
          details as InvalidAction["details"],
        );
      }
    }

    if (e.code === "expected_turn_mismatch") {
      // §7.2 : `action_id` inconnu **et** tour périmé ⇒ un autre onglet a joué. On resynchronise
      // avant de rendre la main : sans ça, le clic suivant repartirait sur le même tour périmé.
      await this.#resynchroniser();
      return new Desynchronise(
        "Un autre onglet a joué cette partie : ton écran vient d'être remis à jour. " +
          "Regarde où tu en es avant de rejouer.",
      );
    }

    if (e.code === "run_not_active") {
      await this.#resynchroniser();
      return new InvalidAction("run_not_active");
    }

    if (e.code === "invalid_timeline") {
      return new Error(`Atelier refusé par le serveur : ${e.message}`);
    }

    return e;
  }

  /** Recharge l'état depuis le serveur, sans rien décider. Silencieux si le réseau retombe. */
  async #resynchroniser(): Promise<void> {
    try {
      await this.charger();
    } catch {
      // On garde le miroir précédent : il est périmé, mais le message de désynchronisation
      // est déjà parti, et écraser l'écran par du vide serait pire.
    }
  }

  /** Adopte une run venue de `/runs/active` : état serveur + métadonnées d'interface locales. */
  #adopter(actif: RunActive): RunSnapshot {
    const meta = lireMeta();
    const memoire = meta.runs[actif.run.id] ?? { journal: [], unlocksSeen: [] };
    const snapshot = this.#composer(actif.run, actif.state, {
      journal: memoire.journal,
      // Un premier chargement (rien en mémoire) ne doit pas dérouler six annonces d'étages
      // d'un coup : on considère comme « déjà vus » ceux qui sont ouverts à l'arrivée.
      unlocksSeen:
        memoire.journal.length === 0 && memoire.unlocksSeen.length === 0
          ? nouveauxEtages(actif.state, [])
          : memoire.unlocksSeen,
    });
    this.#poser(snapshot, actif.turn);
    return snapshot;
  }

  /**
   * Le `RunSnapshot` du contrat, assemblé depuis ce que le serveur envoie. Le `turn`, lui, ne
   * rentre pas dans le snapshot : il est le compteur de `run_state`, pas une donnée d'affichage
   * — il vit dans `#turn` et ne sert qu'à `expected_turn`.
   */
  #composer(run: RunApi, state: GameState, memoire: MetaRun): RunSnapshot {
    return {
      runId: run.id,
      channelName: run.channel_name,
      // ⟦A-4 §2⟧ Le genre est **dans l'état** depuis qu'il est mécanique : on le lit là, et pas
      // dans une métadonnée qu'il faudrait tenir en double.
      genre: (state.genre as GenreAvatar | null) ?? null,
      mode: run.mode as RunMode,
      // ⚠️ **Le seed n'existe pas côté client en mode compte, et c'est voulu** : c'est le secret
      // qui rend les événements et les patterns d'Atelier imprévisibles (§5.4). Les mini-jeux
      // ne s'en servent plus ici : c'est `GET /atelier-pattern` qui les sert.
      seed: "",
      state,
      journal: memoire.journal,
      unlocksSeen: memoire.unlocksSeen,
      contentVersion: run.content_version,
      createdAt: new Date(run.created_at).toISOString(),
      bacASable: false,
    };
  }

  /** Range le miroir et la mémoire d'interface. Un seul chemin d'écriture, comme en local. */
  #poser(run: RunSnapshot, turn: number): void {
    this.#run = run;
    this.#turn = turn;
    const meta = lireMeta();
    meta.dernier = run.runId;
    meta.runs[run.runId] = { journal: run.journal, unlocksSeen: run.unlocksSeen };
    ecrireMeta(meta);
  }

  /** La réponse de §5.5 → le `DispatchResult` que les écrans consomment déjà. */
  #appliquer(precedent: RunSnapshot, reponse: ReponseAction, action?: ActionType): DispatchResult {
    const state = reponse.state;

    let id = precedent.journal.reduce((max, e) => Math.max(max, e.id), 0) + 1;
    // La trame du moteur (`log`) arrive maintenant dans le corps de §5.5 : le journal la lit
    // au lieu de la reconstituer. C'est le même appel que le bac à sable — un seul récit.
    // ⟦C57 · lot 3⟧ Même appel que le bac à sable (parité A-32) : le récap des jours rejoint le Journal.
    const nouvellesEntrees = entreesDeResolution(
      reponse.events,
      reponse.log ?? [],
      state,
      id,
      reponse.recap ? { recap: reponse.recap, action } : null,
    );
    id += nouvellesEntrees.length;

    const deverrouillages = nouveauxEtages(state, precedent.unlocksSeen);
    for (const cle of deverrouillages) {
      const e = etage(cle);
      nouvellesEntrees.push(
        entreeInterface(state, id++, ligneDeverrouillage(e)),
      );
    }

    const journal = [...precedent.journal, ...nouvellesEntrees];
    const suivante: RunSnapshot = {
      ...precedent,
      channelName: reponse.run.channel_name,
      state,
      journal: limiterJournal(journal),
      unlocksSeen: [...precedent.unlocksSeen, ...deverrouillages],
    };
    this.#poser(suivante, reponse.turn);

    const video = reponse.video;
    const progression: Progression = reponse.progression;

    return {
      run: suivante,
      events: reponse.events,
      log: reponse.log ?? [],
      nouvellesEntrees,
      deverrouillages,
      videoPubliee: video ? idMoteurDepuisApi(precedent.runId, video.id) : null,
      // Le bloc `progression` arrive **tel quel** du serveur : paliers franchis, retour au
      // jalon (§7.3), fin verrouillée, victoire gravée avec son score sur 1 000. C'est
      // précisément ce que le bac à sable ne sait pas produire.
      progression,
      // ⟦A-16⟧ Le récapitulatif tel que le moteur l'a **mesuré**, transmis par §5.5. Il se
      // consomme, il ne se recalcule pas : le recomposer serait « la faute la plus grave
      // possible » (`gameClient.ts`) — l'écran mentirait sans que rien ne le signale.
      recap: reponse.recap ?? null,
      // ⟦SPEC-PRODUCTEUR §5.5 / A-66⟧ Le pendant serveur de la ligne du bac à sable.
      delegation: reponse.delegation ?? null,
    };
  }
}
